import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { RefreshCw, Calendar, Fish, TrendingUp, ChevronRight, ChevronDown, Filter } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useMembership } from '@/hooks/useMembership';
import { usePonds } from '@/hooks/usePonds';
import { RoleGuard } from '@/components/layout/RoleGuard';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';

interface Cycle {
  id: string;
  pond_id: string;
  pond_name: string;
  cycle_number: number;
  species: string;
  stocking_date: string | null; // null while preparing (#191)
  prep_start_date?: string | null;
  prep_day?: number | null;
  stocking_count: number | null;
  status: 'preparing' | 'active' | 'completed' | 'cancelled';
  doc?: number | null;
  latest_abw?: number;
  survival_rate?: number;
}

interface CyclesSearchParams {
  pond?: string;
  cycle?: string;
  status?: 'active' | 'all';
}

export const Route = createFileRoute('/_app/cycles')({
  validateSearch: (search: Record<string, unknown>): CyclesSearchParams => ({
    pond: typeof search.pond === 'string' ? search.pond : undefined,
    cycle: typeof search.cycle === 'string' ? search.cycle : undefined,
    status: search.status === 'all' ? 'all' : undefined,
  }),
  component: () => (
    <RoleGuard allowedRoles={['supervisor', 'owner', 'operator']}>
      <CyclesPage />
    </RoleGuard>
  ),
});

function CyclesPage() {
  const { membership } = useMembership();
  const { pond: filterPondId, cycle: filterCycleNum } = Route.useSearch();
  const navigate = Route.useNavigate();
  const [showCompleted, setShowCompleted] = useState(false);

  // Fetch ponds to get names
  const { data: pondsData } = usePonds(membership?.farmId);

  const { data: cyclesData, isLoading } = useQuery({
    queryKey: ['cycles', membership?.farmId],
    queryFn: async () => {
      const response = await api.get<{ data: Cycle[] }>(
        `/api/v1/farms/${membership?.farmId}/cycles`
      );
      return Array.isArray(response) ? response : response?.data || [];
    },
    enabled: !!membership?.farmId,
  });

  // Create pond name lookup
  const pondNames = (pondsData || []).reduce((acc, pond) => {
    acc[pond.id] = pond.name;
    return acc;
  }, {} as Record<string, string>);

  // Enrich cycles with pond names
  const allCycles = (cyclesData || []).map(cycle => ({
    ...cycle,
    pond_name: pondNames[cycle.pond_id] || `Pond ${cycle.pond_id.slice(0, 8)}`,
  }));

  // Apply filters
  const filteredCycles = allCycles.filter(c => {
    if (filterPondId && c.pond_id !== filterPondId) return false;
    if (filterCycleNum && c.cycle_number !== Number(filterCycleNum)) return false;
    return true;
  });

  const preparingCycles = filteredCycles.filter((c) => c.status === 'preparing');
  const activeCycles = filteredCycles.filter((c) => c.status === 'active');
  const completedCycles = filteredCycles.filter((c) => c.status === 'completed' || c.status === 'harvested');

  // Build unique ponds list for filter dropdown
  const pondsWithCycles = [...new Set(allCycles.map(c => c.pond_id))].map(id => ({
    id,
    name: pondNames[id] || `Pond ${id.slice(0, 8)}`,
  })).sort((a, b) => a.name.localeCompare(b.name));

  // Build unique cycle numbers for filter dropdown
  const cycleNumbers = [...new Set(allCycles.map(c => c.cycle_number))].sort((a, b) => a - b);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <LoadingSpinner size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Cycles</h1>
          <p className="mt-1 text-sm text-gray-500">
            Track your production cycles
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="ok" size="md">
            {activeCycles.length} Active
          </Badge>
          {preparingCycles.length > 0 && (
            <Badge variant="neutral" size="md">
              {preparingCycles.length} Preparing
            </Badge>
          )}
          {completedCycles.length > 0 && (
            <Badge variant="neutral" size="md">
              {completedCycles.length} Completed
            </Badge>
          )}
        </div>
      </div>

      {/* Filter Bar */}
      <div className="flex items-center gap-3">
        <Filter className="w-4 h-4 text-gray-400" />
        <select
          value={filterPondId || ''}
          onChange={(e) => {
            navigate({
              search: (prev) => ({
                ...prev,
                pond: e.target.value || undefined,
              }),
            });
          }}
          className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="">All Ponds</option>
          {pondsWithCycles.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        <select
          value={filterCycleNum || ''}
          onChange={(e) => {
            navigate({
              search: (prev) => ({
                ...prev,
                cycle: e.target.value || undefined,
              }),
            });
          }}
          className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="">All Cycles</option>
          {cycleNumbers.map(num => (
            <option key={num} value={String(num)}>Cycle #{num}</option>
          ))}
        </select>
      </div>

      {/* Preparing Cycles */}
      {preparingCycles.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Preparing</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {preparingCycles.map((cycle) => (
              <CycleCard key={cycle.id} cycle={cycle} />
            ))}
          </div>
        </div>
      )}

      {/* Active Cycles */}
      {activeCycles.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Active Cycles</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {activeCycles.map((cycle) => (
              <CycleCard key={cycle.id} cycle={cycle} />
            ))}
          </div>
        </div>
      )}

      {/* Completed Cycles - Collapsible */}
      {completedCycles.length > 0 && (
        <div className="space-y-4">
          <button
            onClick={() => setShowCompleted(!showCompleted)}
            className="flex items-center gap-2 text-lg font-semibold text-gray-700 hover:text-gray-900 transition-colors"
          >
            <ChevronDown
              className={cn(
                'w-5 h-5 transition-transform',
                !showCompleted && '-rotate-90'
              )}
            />
            Completed Cycles
            <Badge variant="neutral" size="sm">
              {completedCycles.length}
            </Badge>
          </button>
          {showCompleted && (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {completedCycles.map((cycle) => (
                <CycleCard key={cycle.id} cycle={cycle} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {filteredCycles.length === 0 && (
        <Card>
          <Card.Body className="text-center py-12">
            <RefreshCw className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-2">
              {filterPondId ? 'No cycles found for this pond' : 'No cycles found'}
            </p>
            <p className="text-sm text-gray-400">
              Start a new cycle from a pond's detail page
            </p>
          </Card.Body>
        </Card>
      )}
    </div>
  );
}

function CycleCard({ cycle }: { cycle: Cycle }) {
  // DOC 1 = stocking day (#191)
  const doc = cycle.doc ?? (cycle.stocking_date
    ? Math.floor((new Date().getTime() - new Date(cycle.stocking_date).getTime()) / (1000 * 60 * 60 * 24)) + 1
    : null);
  const started = cycle.stocking_date ?? cycle.prep_start_date;

  return (
    <Link
      to="/ponds/$pondId"
      params={{ pondId: cycle.pond_id }}
      className="block"
    >
      <Card className="hover:shadow-md transition-shadow cursor-pointer">
        <Card.Body className="p-4">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="font-semibold text-gray-900">{cycle.pond_name}</h3>
              <p className="text-sm text-gray-500">Cycle #{cycle.cycle_number}</p>
            </div>
            <Badge
              variant={cycle.status === 'active' ? 'ok' : 'neutral'}
              size="sm"
            >
              {cycle.status === 'preparing' ? 'Preparing' : cycle.status}
            </Badge>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="w-4 h-4 text-gray-400" />
              <span className="text-gray-600">
                {doc !== null ? `DOC ${doc}` : cycle.prep_day ? `Prep day ${cycle.prep_day}` : '—'}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Fish className="w-4 h-4 text-gray-400" />
              <span className="text-gray-600">
                {cycle.stocking_count ? `${cycle.stocking_count.toLocaleString()} PL stocked` : 'Not stocked yet'}
              </span>
            </div>
            {cycle.latest_abw && (
              <div className="flex items-center gap-2 text-sm">
                <TrendingUp className="w-4 h-4 text-gray-400" />
                <span className="text-gray-600">{cycle.latest_abw.toFixed(1)}g ABW</span>
              </div>
            )}
            {cycle.survival_rate && (
              <div className="flex items-center gap-2 text-sm">
                <span className={cn(
                  "text-xs font-medium px-1.5 py-0.5 rounded",
                  cycle.survival_rate >= 80 ? "bg-green-100 text-green-700" :
                  cycle.survival_rate >= 60 ? "bg-yellow-100 text-yellow-700" :
                  "bg-red-100 text-red-700"
                )}>
                  {cycle.survival_rate.toFixed(0)}% SR
                </span>
              </div>
            )}
          </div>

          <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between text-sm">
            <span className="text-gray-500">
              {started ? `Started ${new Date(started).toLocaleDateString()}` : ''}
            </span>
            <ChevronRight className="w-4 h-4 text-gray-400" />
          </div>
        </Card.Body>
      </Card>
    </Link>
  );
}
